import React from "react";
import { SmartLink } from "./SiteHeader";

const money = (n) => `$${Math.round(n).toLocaleString("en-CA")}`;

// City × first-home numbers from the market snapshot's `buying` block (min-down scenario). Makes each city page genuinely local.
export function CityFirstHomeNumbers({ city, citySlug, snap, calcSlug }) {
  const provincial = !citySlug;
  const b = snap?.buying;
  const min = b?.scenarios?.find((s) => s.key === "minimum");
  if (!b || !min) return null;
  const rows = [
    ["row", "Townhouse / row"], ["apartment", "Condo / apartment"], ["detached", "Detached"],
  ].map(([k, label]) => [label, b.by_type?.[k]?.minimum, snap.monthly?.[k]?.avg_price]).filter(([, m]) => m);
  const rowLabel = (l) => (l === "Detached" ? "Detached / house" : l);
  return (
    <section className="city-numbers" data-testid="city-first-home-numbers">
      <div className="wrap">
        <div className="city-numbers-head">
          <div>
            <div className="eyebrow">Your first home in {city} · by the numbers</div>
            <h2>What “getting in” actually costs {snap.is_proxy ? "around" : provincial ? "across" : "in"} {city}.</h2>
            <p>{snap.is_proxy ? `The Alberta Real Estate Association doesn’t break out ${city} on its own, so we’re working from the Alberta-wide numbers` : provincial ? "We took what actually sold across Alberta" : `We took what actually sold in ${snap.area_name}`} for {snap.period_label}, assumed the smallest down payment allowed, stretched it over {b.amort_years} years at {b.rate}%, and did the math so you don’t have to. Your real numbers will shift with your credit, your income and the home you fall for—think of this as the starting line, not the finish.</p>
          </div>
          <SmartLink className="btn light" href={`/calculators/${calcSlug || "affordability"}/`} data-testid="city-numbers-calc">Run your own numbers</SmartLink>
        </div>
        <div className="city-numbers-grid">
          <div className="city-stat big"><span className="tag">Average price</span><strong>{money(b.price)}</strong><em>{snap.monthly?.total?.avg_price_yoy > 0 ? "+" : ""}{snap.monthly?.total?.avg_price_yoy ?? 0}% vs last year</em></div>
          <div className="city-stat"><span className="tag">Minimum down payment</span><strong>{money(min.down)}</strong><em>{min.down_pct}% down · CMHC premium {money(min.cmhc_premium)}</em></div>
          <div className="city-stat"><span className="tag">Estimated monthly payment</span><strong>{money(min.monthly_payment)}</strong><em>Mortgage only—add tax, heat and condo fees</em></div>
          <div className="city-stat"><span className="tag">Household income to qualify</span><strong>{money(min.income_needed)}</strong><em>Stress-tested at {b.qualifying_rate}%</em></div>
        </div>
        {rows.length > 0 && (
          <div className="city-types" data-testid="city-numbers-by-type">
            {rows.map(([label, m, price]) => (
              <div className="city-type" key={label} data-testid={`city-type-${label.split(" ")[0].toLowerCase()}`}>
                <span className="city-type-label">{rowLabel(label)}</span><strong>{price ? money(price) : "—"}</strong><em>{money(m.down)} down · {money(m.monthly_payment)}/mo</em>
                <b className="city-type-income">{money(m.income_needed)} household income to qualify</b>
              </div>
            ))}
          </div>
        )}
        <p className="city-numbers-foot">Source: {snap.source_label || snap.area_name} · {snap.period_label}.
          {provincial
            ? <><SmartLink href="/rates">See today’s Alberta rates →</SmartLink> <SmartLink href="/locations/">Pick your community for local numbers →</SmartLink></>
            : <><SmartLink href={`/${citySlug}/mortgage-rates/`}>See today’s {city} rates →</SmartLink> <SmartLink href={`/${citySlug}/`}>More on the {city} market →</SmartLink></>}
        </p>
      </div>
    </section>
  );
}
